import React, { useState } from 'react';
import { motion } from 'framer-motion';
import GlassCard from '../components/UI/GlassCard';
import CyberButton from '../components/UI/CyberButton';
import HUDElement from '../components/UI/HUDElement';
import NeonText from '../components/UI/NeonText';
import { useScrollAnimation, scrollVariants } from '../hooks/useScrollAnimation';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        projectType: '',
        budget: '',
        timeline: '',
        message: ''
    });
    const { ref: heroRef, controls: heroControls } = useScrollAnimation();

    const contactMethods = [
        {
            icon: '📧',
            method: 'Email',
            details: 'contact@techanime.dev',
            response: 'Respuesta en 2-4 horas',
            description: 'Para consultas generales y propuestas iniciales'
        },
        {
            icon: '💬',
            method: 'Matrix Chat',
            details: '@techanime:matrix.org',
            response: 'Tiempo real',
            description: 'Comunicación encriptada para discusiones técnicas'
        },
        {
            icon: '📞',
            method: 'Signal Secure',
            details: '+1-555-TECH-AI',
            response: '24/7 para emergencias',
            description: 'Llamadas y mensajes seguros'
        },
        {
            icon: '🏢',
            method: 'Oficinas Globales',
            details: 'Tokio, SV, Berlín',
            response: 'Agendar visita',
            description: 'Reuniones presenciales y demostraciones'
        }
    ];

    const projectTypes = [
        'Desarrollo de IA',
        'Ciberseguridad',
        'Realidad Extendida',
        'Blockchain/Web3',
        'Internet de las Cosas',
        'Desarrollo Software',
        'Consultoría Técnica',
        'Otro'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Aquí iría la lógica de envío del formulario
        console.log('Datos del formulario:', formData);
    };

    return (
        <div className="contact-page">
            {/* Hero Section */}
            <motion.section
                className="page-hero"
                ref={heroRef}
                initial="hidden"
                animate={heroControls}
                variants={scrollVariants}
            >
                <div className="container">
                    <div className="hero-content">
                        <NeonText variant="magenta" size="large" animated={true}>
                            // INICIAR_COMUNICACION
                        </NeonText>
                        <h1 className="hero-title">
                            Conecta con Nuestro
                            <span className="accent"> Sistema</span>
                        </h1>
                        <p className="hero-description">
                            Establezcamos un canal seguro para discutir tu proyecto.
                            Nuestro equipo está listo para analizar tus requerimientos
                            y proponer soluciones tecnológicas avanzadas.
                        </p>

                        <div className="hero-stats">
                            <HUDElement label="Respuesta Promedio" value={2} unit="h" />
                            <HUDElement label="Proyectos Iniciados" value={25} unit="/mes" />
                            <HUDElement label="Satisfacción Cliente" value={99.8} unit="%" />
                            <HUDElement label="Soporte Activo" value={24} unit="/7" />
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Contact Methods */}
            <section className="section methods-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="cyan" size="large">
                            // CANALES_COMUNICACION
                        </NeonText>
                        <h2>Múltiples Vías de Contacto</h2>
                        <p>Selecciona el método que mejor se adapte a tus necesidades de seguridad y urgencia</p>
                    </motion.div>

                    <div className="contact-methods-grid">
                        {contactMethods.map((method, index) => (
                            <motion.div
                                key={method.method}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassCard className="method-card">
                                    <div className="method-icon">{method.icon}</div>
                                    <h3>{method.method}</h3>
                                    <p className="method-details">{method.details}</p>
                                    <p className="method-response">{method.response}</p>
                                    <p className="method-description">{method.description}</p>
                                    <CyberButton variant="outline" size="small">
                                        Conectar
                                    </CyberButton>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form */}
            <section className="section form-section">
                <div className="container">
                    <div className="form-container">
                        <motion.div
                            className="form-header"
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <NeonText variant="magenta" size="large">
                                // FORMULARIO_PROYECTO
                            </NeonText>
                            <h2>Solicitar Análisis de Proyecto</h2>
                            <p>
                                Proporciona detalles sobre tu iniciativa para que podamos
                                asignar los recursos adecuados y preparar una propuesta
                                personalizada.
                            </p>

                            <div className="form-features">
                                <div className="feature">
                                    <span className="feature-icon">🔒</span>
                                    <span>Comunicación Encriptada</span>
                                </div>
                                <div className="feature">
                                    <span className="feature-icon">⚡</span>
                                    <span>Respuesta en 24 Horas</span>
                                </div>
                                <div className="feature">
                                    <span className="feature-icon">🎯</span>
                                    <span>Análisis Sin Costo</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="form-content"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <GlassCard>
                                <form onSubmit={handleSubmit} className="contact-form">
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label htmlFor="name">Nombre Completo *</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="cyber-input"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="email">Email Corporativo *</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="cyber-input"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="company">Organización</label>
                                            <input
                                                type="text"
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="cyber-input"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="projectType">Tipo de Proyecto *</label>
                                            <select
                                                id="projectType"
                                                name="projectType"
                                                value={formData.projectType}
                                                onChange={handleInputChange}
                                                required
                                                className="cyber-select"
                                            >
                                                <option value="">Seleccionar categoría</option>
                                                {projectTypes.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="budget">Presupuesto Estimado</label>
                                            <select
                                                id="budget"
                                                name="budget"
                                                value={formData.budget}
                                                onChange={handleInputChange}
                                                className="cyber-select"
                                            >
                                                <option value="">Seleccionar rango</option>
                                                <option value="5k-15k">$5,000 - $15,000</option>
                                                <option value="15k-50k">$15,000 - $50,000</option>
                                                <option value="50k-100k">$50,000 - $100,000</option>
                                                <option value="100k+">$100,000+</option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="timeline">Linea de Tiempo</label>
                                            <select
                                                id="timeline"
                                                name="timeline"
                                                value={formData.timeline}
                                                onChange={handleInputChange}
                                                className="cyber-select"
                                            >
                                                <option value="">Seleccionar duración</option>
                                                <option value="1-3m">1-3 Meses</option>
                                                <option value="3-6m">3-6 Meses</option>
                                                <option value="6-12m">6-12 Meses</option>
                                                <option value="12m+">12+ Meses</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group full-width">
                                        <label htmlFor="message">Descripción del Proyecto *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows="6"
                                            placeholder="Describe tu proyecto, objetivos, desafíos técnicos y cualquier requerimiento específico..."
                                            className="cyber-textarea"
                                        ></textarea>
                                    </div>

                                    <div className="form-actions">
                                        <CyberButton
                                            type="submit"
                                            variant="cyan"
                                            size="large"
                                            className="submit-button"
                                        >
                                            <span className="button-text">Enviar Solicitud</span>
                                            <span className="button-arrow">⟶</span>
                                        </CyberButton>

                                        <div className="form-note">
                                            <span className="note-icon">💡</span>
                                            Todos los datos se procesan de forma segura y encriptada
                                        </div>
                                    </div>
                                </form>
                            </GlassCard>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="section faq-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="cyan" size="large">
                            // PREGUNTAS_FRECUENTES
                        </NeonText>
                        <h2>Información del Sistema</h2>
                        <p>Respuestas a consultas comunes sobre nuestro proceso de trabajo</p>
                    </motion.div>

                    <div className="faq-grid">
                        {[
                            {
                                question: "¿Cuál es el tiempo promedio de desarrollo?",
                                answer: "Varía según complejidad: proyectos simples 2-4 semanas, sistemas complejos 3-6 meses. Evaluamos durante la consulta inicial."
                            },
                            {
                                question: "¿Ofrecen soporte post-implementación?",
                                answer: "Sí, todos los proyectos incluyen 3 meses de soporte gratuito y opciones de mantenimiento continuo."
                            },
                            {
                                question: "¿Trabajan con tecnologías específicas?",
                                answer: "Desarrollamos con las tecnologías más avanzadas, pero nos adaptamos a los requisitos técnicos de cada proyecto."
                            },
                            {
                                question: "¿Cómo manejan la seguridad de los datos?",
                                answer: "Implementamos encriptación end-to-end, protocolos de seguridad avanzados y cumplimos con regulaciones internacionales."
                            }
                        ].map((faq, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassCard className="faq-card">
                                    <h3>{faq.question}</h3>
                                    <p>{faq.answer}</p>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;